import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { getUnreadNotificationsCount } from '../utils/notificationsService';

/**
 * Hook do zarządzania licznikiem nieprzeczytanych powiadomień
 * Automatycznie subskrybuje zmiany w czasie rzeczywistym
 * @returns {Object} { count, isLoading, error }
 */
export function useNotificationCount() {
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const channelsRef = useRef({ pg: null, bc: null });
  const userIdRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  // Load initial count and set up real-time subscription
  useEffect(() => {
    let mounted = true;

    const initializeCount = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Cleanup: removeChannel jest async — bez await ten sam topic zwraca już „subscribed” kanał i .on() po subscribe() rzuca błąd (Strict Mode / TOKEN_REFRESHED).
        const prev = channelsRef.current;
        if (prev.pg) {
          await supabase.removeChannel(prev.pg);
        }
        if (prev.bc) {
          await supabase.removeChannel(prev.bc);
        }
        channelsRef.current = { pg: null, bc: null };

        if (!mounted) return;

        const runId = `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;

        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          if (mounted) {
            setCount(0);
            setIsLoading(false);
          }
          return;
        }

        userIdRef.current = user.id;

        // Get initial count
        const { count: initialCount, error: countError } = await getUnreadNotificationsCount(user.id);
        
        if (countError) {
          throw countError;
        }

        if (mounted) {
          setCount(initialCount || 0);
        }

        // Funkcja pomocnicza do odświeżania licznika
        const refreshCount = async () => {
          if (mounted && userIdRef.current) {
            const { count: newCount, error: refreshError } = await getUnreadNotificationsCount(userIdRef.current);
            
            if (!refreshError && mounted) {
              setCount(newCount || 0);
            } else if (refreshError) {
              console.error('Error refreshing notification count:', refreshError);
            }
          }
        };

        const startPolling = (interval = 3000) => {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }

          pollingIntervalRef.current = setInterval(() => {
            if (mounted && userIdRef.current) {
              refreshCount();
            }
          }, interval);
        };

        const subscribeHandler = (label) => (status, subscribeErr) => {
          if (status === 'SUBSCRIBED' || status === 'CLOSED') return;
          if (status === 'CHANNEL_ERROR') {
            console.error(
              `Notification count realtime (${label}): CHANNEL_ERROR`,
              subscribeErr?.message || subscribeErr
            );
            if (label === 'postgres') {
              startPolling(3000);
            }
            return;
          }
          if (status === 'TIMED_OUT') {
            console.error(`Notification count realtime (${label}): subscription timed out`);
            if (label === 'postgres') {
              startPolling(3000);
            }
          }
        };

        // Kanał 1: wyłącznie postgres_changes (broadcast na tym samym topic potrafił dawać „mismatch between server and client bindings”)
        const pgChannel = supabase
          .channel(`notification_pg_${user.id}_${runId}`)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'notifications',
            },
            (payload) => {
              const changedUserId = payload.new?.user_id || payload.old?.user_id;
              if (changedUserId !== user.id) return;
              setTimeout(() => {
                refreshCount();
              }, 200);
            }
          )
          .subscribe(subscribeHandler('postgres'));

        channelsRef.current.pg = pgChannel;

        // Kanał 2: broadcast (np. trigger / inne źródło) — błąd tutaj nie wyłącza licznika, postgres + polling zostają
        const bcChannel = supabase
          .channel(`notification_bc_${user.id}_${runId}`)
          .on('broadcast', { event: 'notification_count_refresh' }, (payload) => {
            const { user_id: eventUserId } = payload.payload || {};
            if (eventUserId === user.id) {
              setTimeout(() => {
                refreshCount();
              }, 100);
            }
          })
          .subscribe(subscribeHandler('broadcast'));

        channelsRef.current.bc = bcChannel;

        if (mounted) {
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Error initializing notification count:', err);
        if (mounted) {
          setError(err.message);
          setIsLoading(false);
        }
      }
    };

    initializeCount();

    // Nasłuchuj na custom events z NotificationsList (backup - działa zawsze)
    // Nasłuchujemy zarówno na window jak i document żeby działało z popupami
    const handleNotificationsChanged = (event) => {
      const { userId: eventUserId } = event.detail || {};

      if (eventUserId && eventUserId === userIdRef.current) {
        setTimeout(() => {
          getUnreadNotificationsCount(userIdRef.current).then(({ count: newCount, error }) => {
            if (!error && mounted) {
              setCount(newCount || 0);
            } else if (error) {
              console.error('Error refreshing notification count from custom event:', error);
            }
          });
        }, 200);
      }
    };
    
    // Nasłuchuj na window (główny kanał)
    window.addEventListener('notificationsChanged', handleNotificationsChanged);
    
    // Nasłuchuj również na document jako backup (dla popupów)
    document.addEventListener('notificationsChanged', handleNotificationsChanged);

    // Listen to auth state changes (login/logout)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!session) {
        // User logged out
        setCount(0);
        const ch = channelsRef.current;
        void (async () => {
          if (ch.pg) await supabase.removeChannel(ch.pg);
          if (ch.bc) await supabase.removeChannel(ch.bc);
        })();
        channelsRef.current = { pg: null, bc: null };
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        userIdRef.current = null;
      } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        // User logged in or session refreshed
        // Cleanup previous polling if exists
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        initializeCount();
      }
    });

    // Cleanup function
    return () => {
      mounted = false;
      subscription.unsubscribe();
      window.removeEventListener('notificationsChanged', handleNotificationsChanged);
      document.removeEventListener('notificationsChanged', handleNotificationsChanged);
      const { pg, bc } = channelsRef.current;
      channelsRef.current = { pg: null, bc: null };
      void (async () => {
        if (pg) await supabase.removeChannel(pg);
        if (bc) await supabase.removeChannel(bc);
      })();
      // Cleanup polling if exists
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      userIdRef.current = null;
    };
  }, []);

  return { count, isLoading, error };
}
